'use client';

/**
 * The backing store a {@link BrowserStorageWrapper} resolves to at runtime.
 *
 * `as const` union rather than a TS `enum` per the repo standard (no `enum`).
 */
export const StorageMode = {
  LOCAL: 'localStorage',
  SESSION: 'sessionStorage',
  BACKUP: 'backupStorage',
} as const;

export type StorageMode = (typeof StorageMode)[keyof typeof StorageMode];

/**
 * A wrapper to protect against storage access throwing.
 *
 * `localStorage`/`sessionStorage` are not always usable:
 * - Safari and Firefox throw a `SecurityError` when site data is blocked
 *   (see https://developer.mozilla.org/en-US/docs/Web/API/Window/localStorage#securityerror).
 * - Android WebViews expose `localStorage` as `null` when the host app has not
 *   enabled DOM storage, so member access throws a `TypeError`.
 * - During SSR the globals are undefined, so access throws a `ReferenceError`.
 *
 * The wrapper probes for a usable store once, preferring `preferredMode`, and
 * falls back through the remaining browser store to an in-memory map so callers
 * never have to guard the access themselves.
 */
export class BrowserStorageWrapper {
  #storageMode: StorageMode | undefined;
  readonly #preferredMode: StorageMode;
  backupStorage: Record<string, string>;

  constructor(preferredMode: StorageMode = StorageMode.LOCAL) {
    this.#preferredMode = preferredMode;
    this.backupStorage = Object.create(null) as Record<string, string>;
  }

  getStorageMode(): StorageMode {
    if (this.#storageMode) {
      return this.#storageMode;
    }

    // Probe the preferred browser store first, then the other, then give up to
    // the in-memory backup. A `getItem` call forces the access that throws when
    // the store is blocked, null (Android WebView), or absent (SSR).
    const probeOrder =
      this.#preferredMode === StorageMode.SESSION
        ? [StorageMode.SESSION, StorageMode.LOCAL]
        : [StorageMode.LOCAL, StorageMode.SESSION];

    for (const mode of probeOrder) {
      try {
        if (mode === StorageMode.LOCAL) {
          localStorage.getItem('__storage_probe__');
        } else {
          sessionStorage.getItem('__storage_probe__');
        }
        this.#storageMode = mode;
        return this.#storageMode;
      } catch {
        // Store isn't accessible in this environment; try the next one.
      }
    }

    this.#storageMode = StorageMode.BACKUP;
    return this.#storageMode;
  }

  getItem(key: string): string | null {
    const mode = this.getStorageMode();
    try {
      switch (mode) {
        case StorageMode.LOCAL:
          return localStorage.getItem(key);
        case StorageMode.SESSION:
          return sessionStorage.getItem(key);
        case StorageMode.BACKUP:
          return this.backupStorage[key] ?? null;
      }
    } catch {
      this.#storageMode = StorageMode.BACKUP;
      return this.backupStorage[key] ?? null;
    }
  }

  setItem(key: string, value: string): void {
    switch (this.getStorageMode()) {
      case StorageMode.LOCAL:
        localStorage.setItem(key, value);
        break;
      case StorageMode.SESSION:
        sessionStorage.setItem(key, value);
        break;
      case StorageMode.BACKUP:
        this.backupStorage[key] = value;
        break;
    }
  }

  removeItem(key: string): void {
    switch (this.getStorageMode()) {
      case StorageMode.LOCAL:
        localStorage.removeItem(key);
        break;
      case StorageMode.SESSION:
        sessionStorage.removeItem(key);
        break;
      case StorageMode.BACKUP:
        delete this.backupStorage[key];
        break;
    }
  }
}

/**
 * Shared `localStorage`-preferring instance for callers that just want a safe
 * key/value store. Import this rather than constructing your own unless you
 * need a different {@link StorageMode} preference.
 */
export const browserStorage = new BrowserStorageWrapper();
